import type { KeyboardEvent, MouseEvent } from 'react'
import { formatRelativeTime, inferredTagsForNote, type Note, type NoteStatus } from '../domain/note'
import { Icon } from './Icon'
import type { NoteAsset } from '../assets/assetTypes'
import { ImageAttachments } from './ImageAttachments'
import { TagLabel } from './TagLabel'
import { deriveNoteUploadStatus, UploadStatusIndicator } from './UploadStatusIndicator'
import { noteCardTransitionName } from '../utils/noteCardTransition'
import { hideMarkdownPreviewArtifacts } from '../utils/markdownText'

interface NoteCardProps {
  note: Note
  copied?: boolean
  compact?: boolean
  transitionScope?: string
  onCopy: (note: Note) => void
  onBump: (note: Note) => void
  onOpen: (note: Note) => void
  onStatusChange: (note: Note, status: NoteStatus) => void
  assets?: NoteAsset[]
  remoteAssetUrls?: ReadonlyMap<string, string>
  expectedCount?: number
  noteSyncAttemptCount?: number
  uploadStatusNow?: number
  onRetryUpload: (noteId: string, assets: NoteAsset[]) => void
}

function plainSnippet(markdown: string): string {
  return hideMarkdownPreviewArtifacts(markdown)
    .replace(/https?:\/\/\S+/g, '')
    .replace(/[#*_=~`>\[\]()-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 170)
}

export function NoteCard({
  note,
  copied = false,
  compact = false,
  transitionScope = 'preview',
  onCopy,
  onBump,
  onOpen,
  onStatusChange,
  assets = [],
  remoteAssetUrls,
  expectedCount,
  noteSyncAttemptCount = 0,
  uploadStatusNow,
  onRetryUpload,
}: NoteCardProps) {
  const visibleTags = [...new Set([...note.userTags, ...inferredTagsForNote(note)])]
  const title = hideMarkdownPreviewArtifacts(note.manualTitle ?? note.derivedTitle).trim()
  const snippet = plainSnippet(note.annotation || note.urls[0]?.description || note.body)
  const source = note.urls[0]?.siteName ?? note.urls[0]?.domain?.replace(/^www\./, '')
  const uploadStatus = deriveNoteUploadStatus(note, assets, noteSyncAttemptCount, uploadStatusNow)

  const changeStatus = (event: MouseEvent, status: NoteStatus) => {
    event.stopPropagation()
    onStatusChange(note, status)
  }

  const copy = (event: MouseEvent) => {
    event.stopPropagation()
    onCopy(note)
  }

  const bump = (event: MouseEvent) => {
    event.stopPropagation()
    onBump(note)
  }

  const openWithKeyboard = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' || event.target !== event.currentTarget) return
    event.preventDefault()
    onOpen(note)
  }

  return (
    <article
      className={compact ? 'note-card note-card--compact' : 'note-card'}
      style={{ viewTransitionName: noteCardTransitionName(transitionScope, note.id) }}
      onClick={() => onOpen(note)}
      tabIndex={0}
      onKeyDown={openWithKeyboard}
    >
      {source || (compact && uploadStatus) ? (
        <div className="note-card__meta">
          <span className="note-card__kind">
            {source ? <span>{source}</span> : null}
          </span>
          {compact && uploadStatus ? (
            <span className="note-card__meta-end">
              <UploadStatusIndicator
                status={uploadStatus}
                onRetry={() => onRetryUpload(note.id, assets)}
              />
            </span>
          ) : null}
        </div>
      ) : null}
      <h3>{title}</h3>
      {!compact ? (
        <ImageAttachments
          assets={assets}
          remoteUrls={remoteAssetUrls}
          expectedCount={expectedCount}
          compact
        />
      ) : null}
      {!compact && snippet ? <p>{snippet}</p> : null}
      {!compact ? <div className="note-card__footer">
        <div className="note-card__footer-meta">
          <span
            className="note-card__modified"
            aria-label={`Last edited ${formatRelativeTime(note.modifiedAt)}`}
            title={`Last edited ${new Date(note.modifiedAt).toLocaleString()}`}
          >
            <Icon name="clock" />
            <span>{formatRelativeTime(note.modifiedAt)}</span>
          </span>
          <div className="note-card__tags">
            {visibleTags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className={note.userTags.includes(tag) ? undefined : 'note-card__tag--inferred'}
              ><TagLabel tag={tag} /></span>
            ))}
            {note.expiresAt ? (
              <span className="expiry-label"><Icon name="clock" /> expires</span>
            ) : null}
          </div>
          <UploadStatusIndicator
            status={uploadStatus}
            onRetry={() => onRetryUpload(note.id, assets)}
          />
        </div>
        <div className="note-card__actions">
          <button
            type="button"
            title={copied ? 'Copied' : 'Copy Markdown'}
            aria-label={copied ? 'Markdown copied' : 'Copy note as Markdown'}
            onClick={copy}
          >
            <Icon name={copied ? 'check' : 'copy'} />
          </button>
          <button type="button" title="Bump to top" aria-label="Bump note to top" onClick={bump}>
            <Icon name="bump" />
          </button>
          {note.status !== 'trash' ? (
            <button type="button" title="Move to trash" aria-label="Move note to trash" onClick={(event) => changeStatus(event, 'trash')}>
              <Icon name="trash" />
            </button>
          ) : null}
        </div>
      </div> : (
        <span
          className="note-card__modified note-card__modified--compact"
          aria-label={`Last edited ${formatRelativeTime(note.modifiedAt)}`}
          title={`Last edited ${new Date(note.modifiedAt).toLocaleString()}`}
        >
          <Icon name="clock" />
          <span>{formatRelativeTime(note.modifiedAt)}</span>
        </span>
      )}
    </article>
  )
}
